import * as Sentry from '@sentry/browser';
/*******************
 *    MIXPANEL     *
 *******************/
import mixpanelLib from 'mixpanel-browser/src/loaders/loader-module-with-async-modules';
// https://github.com/mixpanel/mixpanel-js/releases/tag/v2.54.0
import queryString from 'query-string';

import { sha256Hex } from 'my-core/crypto';
import { CURRENT_TIMEZONE_NAME } from 'my-core/date-utils';
import { googleLogout } from 'my-core/google-sign-in';
import { chatSupportOpened } from 'my-core/gtm-events';
import { insertScript } from 'my-core/script-utils';
import { isMobileDevice } from 'my-core/user-agent-utils';

import { ANALYTICS_TRAFFIC, DEVICE_INFO, DEVICE_UUID, RELEASE_VERSION } from './analytics/analytics-constants';
import { installCalendlyLinkTracking } from './analytics/calendly-links';
import { getCampaignParams } from './analytics/campaign-params';
import * as hubspot from './analytics/hubspot';
import { markHuman, whenHuman } from './analytics/human-gate';
import * as posthog from './analytics/posthog';
import { rememberPage } from './analytics/signup-page';

export function install(user, subscription) {
  if (!user.guest) markHuman();
  intercomInstall(user);
  mixpanelInstall(user, subscription);
  posthog.install(user, subscription);
  installCalendlyLinkTracking();
  gaInstall(user);
  facebookInstall(user);
  installShareASale(user);
  installCustomerIO(user);
  installTikTok(user);
  installOpenAI(user);
  installHubspot(user);

  trackPageView(1000);
  sentryIdentify(user);
}

export function pageChange(user, subscription, location) {
  hubspot.update(user);
  intercomUpdate(user, location);
  trackPageView();
}

export function userUpdate(user, subscription) {
  markHuman();
  hubspot.update(user);
  mixpanelUpdate(user, subscription);
  posthog.update(user, subscription);
  shareASaleUpdate(user);
  sentryIdentify(user);
}

export function logout() {
  intercomUninstall();
  analyticsReset();
  CustomerIO.reset();
  googleLogout();
  Sentry.setUser(null);
}

// Who hit the error: the user id (the SDK adds the IP address itself) and a filterable user-type tag.
// Runs on load and again after an in-app sign-in, which previously left Sentry without the user.
function sentryIdentify(user) {
  Sentry.setUser(user.guest ? null : { id: user.id });
  Sentry.setTag('user-type', user.user_type);
}

// try to de-duplicate calls caused by instant redirects by debouncing calls and verify that url is different
let pageViewInitialLoad = true;
let pageViewTimeout = null;
let pageViewHref = null;
function trackPageView(timeout) {
  if (pageViewTimeout) clearTimeout(pageViewTimeout);
  pageViewTimeout = setTimeout(() => {
    const href = window.location.href;
    if (href === pageViewHref) return;
    pageViewHref = href;
    rememberPage(window.location.pathname);
    const initialLoad = pageViewInitialLoad;
    // eslint-disable-next-line no-console
    if (__DEV__) console.log('Track Page View', href, initialLoad);
    whenHuman(() => {
      if (process.env.MIXPANEL_TOKEN) mixpanelLib.track_pageview({ initial_load: initialLoad });
      posthog.pageview({ initial_load: initialLoad });
      CustomerIO.page();
      if (!initialLoad) {
        trackHubspot('setPath', window.location.pathname + window.location.search);
        trackHubspot('trackPageView');
      }
    });
    if (process.env.OPENAI_PIXEL_ID) oaiq('measure', 'page_viewed', { type: 'contents' });
    pageViewInitialLoad = false;
  }, timeout || 400);
}

/**********************
 * GOOGLE TAG MANAGER *
 *********************/
export const dataLayer = {
  push(...args) {
    if (window.gtmDataLayer) window.gtmDataLayer.push(...args);
    else {
      // eslint-disable-next-line no-console
      if (__DEV__) console.log('gtmDataLayer.push', args);
      args[0]?.eventCallback?.();
    }
  },
};

/**********************
 * GOOGLE ANALYTICS   *
 *********************/
const gtagSuperProps = {};

export function gtag(...args) {
  if (args[0] === 'event') args[2] = { ...gtagSuperProps, ...args[2] };
  // eslint-disable-next-line no-console
  if (__DEV__) console.log('gtag:', args);
  if (window.gtag) return window.gtag(...args);
  window.dataLayer ||= [];
  const gtag = (window.gtag = function () {
    window.dataLayer.push(arguments);
  });
  gtag(...args);
}

function gaInstall(user) {
  if (process.env.GOOGLE_ANALYTICS_ID) {
    insertScript(`https://www.googletagmanager.com/gtag/js?id=${process.env.GOOGLE_ANALYTICS_ID}`);
  }
  gtag('js', new Date());
  gtag('set', {
    analytics_source: ANALYTICS_TRAFFIC.source,
    analytics_traffic_type: ANALYTICS_TRAFFIC.trafficType,
    analytics_version: ANALYTICS_TRAFFIC.version,
  });
  gtagSuperProps.user_type = user.user_type;
  gtagSuperProps.user_school_name = user.school_name;

  if (!user.guest) {
    gtag('set', { user_id: user.id });
    gtag('set', 'user_properties', {
      user_type: user.user_type,
      user_school_id: user.school_id,
      user_school_name: user.school_name,
    });
  }
  gtag('config', process.env.GOOGLE_ANALYTICS_ID);
}

/*******************
 *    FACEBOOK     *
 *******************/
function installFbProxy() {
  if (window.fbq) return;
  const n = (window.fbq = function () {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
  });
  if (!window._fbq) window._fbq = n;
  n.push = n;
  n.loaded = !0;
  n.version = '2.0';
  n.queue = [];
}

export function fbevents(...args) {
  // eslint-disable-next-line no-console
  if (__DEV__) console.log('fbevents:', args);
  installFbProxy();
  window.fbq?.(...args);
}

function facebookInstall(user) {
  if (process.env.FB_PIXEL_ID) {
    insertScript('https://connect.facebook.net/en_US/fbevents.js');
  }
  fbevents('init', process.env.FB_PIXEL_ID, user.guest ? {} : { em: user.email });
  fbevents('track', 'PageView');
}

/*******************
 *    INTERCOM     *
 *******************/
function installIntercomProxy() {
  if (window.Intercom) return;
  const Intercom = (window.Intercom = function () {
    Intercom.c(arguments);
  });
  Intercom.q = [];
  Intercom.c = function (args) {
    Intercom.q.push(args);
  };
}

export function Intercom(...args) {
  // eslint-disable-next-line no-console
  if (__DEV__) console.log('Intercom: ', ...args);
  installIntercomProxy();
  window.Intercom(...args);
}

function intercomInstall(user) {
  if (process.env.INTERCOM_APP_ID) {
    insertScript(`https://widget.intercom.io/widget/${process.env.INTERCOM_APP_ID}`);
  }
  Intercom('boot', getBootIntercomSettings(user));
  Intercom('onShow', chatSupportOpened);
}
function intercomUpdate(user) {
  Intercom('update', getUpdateIntercomSettings(user));
}

function intercomUninstall() {
  Intercom('shutdown');
}

function getUpdateIntercomSettings(user) {
  // send last_request_at to make sure intercom is pinged, since it's unlikely that other attributes
  // have changed: https://www.intercom.com/help/en/articles/170-integrate-intercom-in-a-single-page-app
  const last_request_at = parseInt(Date.now() / 1000);
  if (!user.guest) {
    return {
      user_id: user.id,
      email: user.email,
      user_hash: user.ic_user_hash,
      last_request_at,
    };
  } else {
    return { last_request_at };
  }
}

function getBootIntercomSettings(user) {
  const config = { app_id: process.env.INTERCOM_APP_ID };
  if (!user.guest) {
    Object.assign(config, {
      user_id: user.id,
      name: user.name,
      email: user.email,
      created_at: parseInt(new Date(user.created_at).getTime() / 1000),
      user_hash: user.ic_user_hash,
    });
    // this setting will override the visibility conditions set on intercom.io
    if (isMobileDevice()) {
      config.hide_default_launcher = true;
    }
  }
  return config;
}

export const mixpanel = {
  track(...args) {
    // eslint-disable-next-line no-console
    if (__DEV__) console.log('Mixpanel: ', ...args);
    if (mixpanelLib.config) {
      mixpanelLib.track(...args);
    } else {
      // 4th arg may be callback
      if (typeof args[3] === 'function') args[3]();
    }
  },
  identify(...args) {
    if (mixpanelLib.config) {
      mixpanelLib.identify(...args);
    }
  },
  register(...args) {
    if (mixpanelLib.config) {
      mixpanelLib.register(...args);
    }
  },
};
function mixpanelInstall(user, subscription) {
  if (process.env.MIXPANEL_TOKEN) {
    // `skip_first_touch_marketing`, `store_google` config to disable default mixpanel handling of utm params -- register & people.set(_once)
    mixpanelLib.init(process.env.MIXPANEL_TOKEN, {
      skip_first_touch_marketing: true,
      store_google: true,
      loaded: () => {
        const distinctId = mixpanelLib.get_distinct_id();
        const wasIdentified = typeof distinctId === 'number';
        if (wasIdentified && (user.guest || distinctId !== user.id)) {
          mixpanelLib.track('MP Session Auto-Reset', { prev_distinct_id: distinctId, user_id: user.id });
          mixpanelLib.reset();
        }
      },
    });
    const campaignParams = getCampaignParams();
    if (campaignParams) {
      mixpanelLib.people.set(campaignParams.params);
      mixpanelLib.people.set_once(campaignParams.firstParams);
      mixpanelLib.register(campaignParams.params);
    }
    if (!user.guest) {
      mixpanelLib.identify(user.id);
      updateMixpanelUser(user, subscription);
    }
    mixpanelLib.register(
      {
        analytics_source: ANALYTICS_TRAFFIC.source,
        analytics_traffic_type: ANALYTICS_TRAFFIC.trafficType,
        analytics_version: ANALYTICS_TRAFFIC.version,
        user_agent: DEVICE_INFO.ua,
        device_uuid: DEVICE_UUID,
        release_version: RELEASE_VERSION,
      },
      { persistent: false },
    );
  }
}

function mixpanelUpdate(user, subscription) {
  if (process.env.MIXPANEL_TOKEN) {
    updateMixpanelUser(user, subscription);
  }
}

let prevMPUpdateProps = {};
function updateMixpanelUser(user, subscription) {
  const userProps = {
    $email: user.email,
    $created: user.created_at,
    $first_name: user.first_name,
    $last_name: user.last_name,
    $country_code: user.country_code,
    $avatar: user.avatar.is_silhouette ? undefined : user.avatar.normal,
    roles: user.roles,
    school_name: user.school_name,
    school_type: user.school_type,
    school_id: user.school_id,
    subscription_status: subscription?.status,
    subscription_is_premed: subscription?.is_premed_plus,
    paying_user: user.paying_user,
  };
  const regProps = {
    user_type: user.user_type,
    subscription_status: subscription?.status,
    subscription_is_premed: subscription?.is_premed_plus,
    paying_user: user.paying_user,
  };
  if (JSON.stringify(userProps) !== JSON.stringify(prevMPUpdateProps.userProps)) mixpanelLib.people.set(userProps);
  if (JSON.stringify(regProps) !== JSON.stringify(prevMPUpdateProps.regProps))
    mixpanelLib.register(regProps, { persistent: false }); // don't persist, in case session times-out and reset isn't invoked
  prevMPUpdateProps = { userProps, regProps };
}

export function analyticsReset() {
  hubspot.reset();
  // Not initialized where analytics are off (e.g. staff signed in as a student), and reset() then throws.
  if (mixpanelLib.config) {
    mixpanelLib.reset();
  }
  posthog.reset();
}

/*******************
 *  Share-A-Sale   *
 *******************/

let sasPayingCustomer = false;
function installShareASale(user) {
  shareASaleUpdate(user);
  if (process.env.SHAREASALE_MERCHANT_ID) {
    insertScript('https://www.dwin1.com/19038.js');
  }
}
function shareASaleUpdate(user) {
  // this may get updated before ShareASale.purchase is called when purchase is made, so delay
  setTimeout(() => {
    sasPayingCustomer = user.paying_user;
  }, 0);
}

export const ShareASale = {
  // transtype & tracking are required
  _track({ tracking, transtype, ...params }) {
    if (process.env.SHAREASALE_MERCHANT_ID) {
      const img = window.document.createElement('img');
      img.src = `https://www.shareasale.com/sale.cfm?${queryString.stringify({
        merchantID: process.env.SHAREASALE_MERCHANT_ID,
        amount: '0.00',
        ...params,
        transtype,
        tracking,
      })}`;
      img.width = 1;
      img.height = 1;
      window.document.body.appendChild(img);
    }
  },
  lead(user) {
    this._track({ tracking: user.id, transtype: 'lead' });
  },
  purchase({ amount, currency, discountCode, orderId, productId }) {
    const fixedAmt = amount.toFixed(2);
    this._track({
      tracking: orderId,
      amount: fixedAmt,
      currency: currency?.toUpperCase(),
      transtype: 'sale',
      skulist: productId,
      pricelist: fixedAmt,
      quantitylist: 1,
      couponcode: discountCode,
      newcustomer: sasPayingCustomer ? 0 : 1,
    });
  },
};

/*******************
 *   CUSTOMER.IO   *
 *******************/

function installCustomerIOSnippet() {
  const i = 'cioanalytics';
  const analytics = (window[i] ||= []);
  if (!analytics.initialize)
    if (analytics.invoked) console.error('Snippet included twice.');
    else {
      analytics.invoked = !0;
      analytics.methods = [
        'trackSubmit',
        'trackClick',
        'trackLink',
        'trackForm',
        'pageview',
        'identify',
        'reset',
        'group',
        'track',
        'ready',
        'alias',
        'debug',
        'page',
        'once',
        'off',
        'on',
        'addSourceMiddleware',
        'addIntegrationMiddleware',
        'setAnonymousId',
        'addDestinationMiddleware',
      ];
      analytics.factory = function (e) {
        return function () {
          var t = Array.prototype.slice.call(arguments);
          t.unshift(e);
          analytics.push(t);
          return analytics;
        };
      };
      for (var e = 0; e < analytics.methods.length; e++) {
        var key = analytics.methods[e];
        analytics[key] = analytics.factory(key);
      }
      analytics.load = function (key, e) {
        insertScript(`https://cdp.customer.io/v1/analytics-js/snippet/${key}/analytics.min.js`, {
          attrs: { 'data-global-customerio-analytics-key': i },
        });
        analytics._writeKey = key;
        analytics._loadOptions = e;
      };
      analytics.SNIPPET_VERSION = '4.15.3';
      analytics.load(
        process.env.CUSTOMER_IO_WRITE_KEY,
        // Optional: enable anonymous in-app. See https://docs.customer.io/journeys/anonymous-in-app/#enable-anonymous-in-app-messaging.
        {
          integrations: {
            'Customer.io In-App Plugin': {
              anonymousInApp: true,
            },
          },
        },
      );
    }
}

// import { AnalyticsBrowser } from '@customerio/cdp-analytics-browser';
function installCustomerIO(user) {
  if (process.env.CUSTOMER_IO_WRITE_KEY) {
    installCustomerIOSnippet();
    // window.cioanalytics = AnalyticsBrowser.load(
    //   { writeKey: process.env.CUSTOMER_IO_WRITE_KEY },
    //   { integrations: { 'Customer.io In-App Plugin': { anonymousInApp: true } } },
    // );
    if (!user.guest) {
      CustomerIO.identify(user);
    }
  }
}

export const CustomerIO = {
  track(...args) {
    // eslint-disable-next-line no-console
    if (__DEV__) console.log('CustomerIO: ', ...args);
    window.cioanalytics?.track(...args);
  },
  identify(user) {
    window.cioanalytics?.identify(user.id, {
      email: user.email,
      created_at: parseInt(new Date(user.created_at).getTime() / 1000),
      first_name: user.first_name,
      last_name: user.last_name,

      school: user.school_name, // DEPRECATED
      school_id: user.school_id,
      school_name: user.school_name,
      school_short_name: user.school_short_name || user.school_name,
      school_type: user.school_type,
      user_type: user.user_type,

      timezone: CURRENT_TIMEZONE_NAME,

      _timestamp: parseInt(Date.now() / 1000),
    });
  },
  page(...args) {
    window.cioanalytics?.page(...args);
  },
  reset() {
    window.cioanalytics?.reset();
  },
};

/*******************
 *      TikTok     *
 *******************/

export function tikTokTrack(...args) {
  window.ttq?.track(...args);
}

function installTikTok(user) {
  if (process.env.TIKTOK_PIXEL_ID) {
    const t = 'ttq';
    window.TiktokAnalyticsObject = t;
    const ttq = (window[t] ||= []);
    ttq.methods = [
      'page',
      'track',
      'identify',
      'instances',
      'debug',
      'on',
      'off',
      'once',
      'ready',
      'alias',
      'group',
      'enableCookie',
      'disableCookie',
    ];
    ttq.setAndDefer = function (t, e) {
      t[e] = function () {
        t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
      };
    };
    for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
    ttq.instance = function (t) {
      for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]);
      return e;
    };
    ttq.load = function (e, n) {
      const i = 'https://analytics.tiktok.com/i18n/pixel/events.js';
      ttq._i ||= {};
      ttq._i[e] = [];
      ttq._i[e]._u = i;
      ttq._t ||= {};
      ttq._t[e] = +new Date();
      ttq._o ||= {};
      ttq._o[e] = n || {};

      insertScript(`${i}?sdkid=${e}&lib=${t}`);
    };
    ttq.load(process.env.TIKTOK_PIXEL_ID);
    ttq.page();
    if (!user.guest) {
      ttq.identify({
        email: user.email,
        external_id: user.id,
      });
    }
  }
}

/*******************
 *   OPENAI ADS    *
 *******************/

function installOaiqProxy() {
  if (window.oaiq) return;
  const q = (window.oaiq = function () {
    q.q.push(arguments);
  });
  q.q = [];
}

export function oaiq(...args) {
  // eslint-disable-next-line no-console
  if (__DEV__) console.log('oaiq:', args);
  installOaiqProxy();
  window.oaiq?.(...args);
}

function installOpenAI(user) {
  if (process.env.OPENAI_PIXEL_ID) {
    installOaiqProxy();
    insertScript('https://bzrcdn.openai.com/sdk/oaiq.min.js');
    oaiq('init', { pixelId: process.env.OPENAI_PIXEL_ID });
    if (!user.guest) openAiIdentify(user);
  }
}

// OpenAI rejects raw identifiers, so re-init with the digests once they resolve
async function openAiIdentify(user) {
  try {
    const [email_sha256, external_id_sha256] = await Promise.all([sha256Hex(user.email), sha256Hex(`${user.id}`)]);
    if (!email_sha256 && !external_id_sha256) return;
    oaiq('init', {
      pixelId: process.env.OPENAI_PIXEL_ID,
      user: { email_sha256, external_id_sha256, country: user.country_code },
    });
  } catch (e) {
    // a failed digest only costs ad match quality, and the pixel is already initialized without it
    // eslint-disable-next-line no-console
    if (__DEV__) console.log('oaiq identify failed:', e);
  }
}

/*******************
 *     Hubspot     *
 *******************/

function trackHubspot(...args) {
  (window._hsq ||= []).push(args);
}

function installHubspot(user) {
  hubspot.update(user);
  if (process.env.HUBSPOT_PORTAL_ID) {
    insertScript(`//js.hs-scripts.com/${process.env.HUBSPOT_PORTAL_ID}.js`, { attrs: { id: 'hs-script-loader' } });
    if (!user.guest && user.hubspot_contact_id) {
      trackHubspot('identify', { email: user.email, id: user.id });
    }
  }
}
// <script type="text/javascript" id="hs-script-loader" async defer src="//js.hs-scripts.com/{hubId}.js"></script>
